import { BadgeCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { assistantsOneSlides } from "../../data/landingData";
import { FadeIn } from "../ui/FadeIn";

export function AgentesSection() {
  const [activeSlide, setActiveSlide] = useState(0);
  const totalSlides = assistantsOneSlides.length;
  const currentSlide = assistantsOneSlides[activeSlide];

  const goToSlide = (index: number) => {
    if (index < 0) {
      setActiveSlide(totalSlides - 1);
      return;
    }
    if (index >= totalSlides) {
      setActiveSlide(0);
      return;
    }
    setActiveSlide(index);
  };

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % totalSlides);
    }, 11_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [totalSlides]);

  return (
    <section id="asistentes" className="py-16 sm:py-20">
      <div className="section-shell">
        <FadeIn className="text-center">
          <h2 className="text-3xl font-bold uppercase tracking-tight sm:text-4xl">
            Asistentes 1.0
          </h2>
        </FadeIn>

        <div className="relative mt-10 px-10 lg:px-14">
          <button
            type="button"
            onClick={() => goToSlide(activeSlide - 1)}
            className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full border border-borderSoft bg-white p-2 text-brand-700 shadow-sm transition hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 lg:left-3"
            aria-label="Slide anterior"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => goToSlide(activeSlide + 1)}
            className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full border border-borderSoft bg-white p-2 text-brand-700 shadow-sm transition hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 lg:right-3"
            aria-label="Siguiente slide"
          >
            <ChevronRight size={18} />
          </button>
          <div key={currentSlide.id} className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <FadeIn>
              <p className="text-3xl font-extrabold leading-tight text-brand-700 sm:text-4xl">
                {currentSlide.name}
                <br />
                <span className="text-2xl sm:text-3xl">{currentSlide.role}</span>
              </p>
              <p className="mt-4 max-w-xl text-base text-ink sm:text-lg">{currentSlide.summary}</p>
              <ul className="mt-6 space-y-3">
                {currentSlide.features.map((item, index) => (
                  <motion.li
                    key={item}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08, duration: 0.35 }}
                    className="flex items-start gap-3 rounded-2xl border border-borderSoft bg-white p-3 shadow-sm"
                  >
                    <BadgeCheck className="mt-0.5 text-emerald-500" size={18} />
                    <span className="text-sm font-medium text-ink sm:text-base">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </FadeIn>

            <FadeIn delay={0.12}>
              <img
                src={currentSlide.image ?? "/kela-landing.png"}
                alt={`${currentSlide.name} asistente`}
                className="mx-auto w-full max-w-xs object-cover"
              />
            </FadeIn>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2">
          {assistantsOneSlides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => goToSlide(index)}
              className={`h-2.5 rounded-full transition ${
                activeSlide === index ? "w-7 bg-brand-700" : "w-2.5 bg-brand-100 hover:bg-brand-500"
              }`}
              aria-label={`Ir al slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
